import type { ReactNode } from "react";

import {
  AlertDialog,
  Button,
  Dialog,
  Flex,
  Heading,
  Tabs,
  Text,
  TextField,
  VisuallyHidden,
} from "@radix-ui/themes";

import { cn } from "~/v2/utils/cn";
import { Field } from "~/v3-poc/components/Field";
import type { WorkspaceActiveView } from "./shared/workspace/workspaceSelection.controller";
import {
  WorkspaceTitleLeadingSlotProvider,
  WorkspaceToolbarSlotProvider,
} from "./shared/workspaceToolbarSlot";
import type { WorkspaceTab, WorkspaceTabId } from "./Workspace.controller";
import type { WorkspaceNavController } from "./WorkspaceNav.controller";
import {
  OVERVIEW_NAV_VALUE,
  type WorkspaceNavMode,
  WorkspaceNavPresentation,
} from "./WorkspaceNav.pres";
import type { WorkspaceShellController } from "./WorkspaceShell.controller";

export interface WorkspacePresentationProps extends WorkspaceShellController {
  activeView: WorkspaceActiveView;
  tabs: ReadonlyArray<WorkspaceTab>;
  canCloseTab: boolean;
  selectWorkspace: (tab: WorkspaceTabId) => void;
  selectOverview: () => void;
  openWorkspace: (tab: WorkspaceTabId) => void;
  activePanel: ReactNode;
  navMode: WorkspaceNavMode;
  navCollapsed: boolean;
  onToggleNavCollapsed: () => void;
  onOpenSettings: () => void;
  /** Account-level controls for the nav's trailing edge, injected connected. */
  accountActions?: ReactNode;
  /** Drag and scroll state for the top tab strip. */
  nav: WorkspaceNavController;
}

const flexFill = "flex min-h-0 grow flex-col";

interface WorkspaceBoardProps {
  title: string;
  /** The overview names itself through its cards, so its board header is mute. */
  titleHidden?: boolean;
  toolbarSlot: HTMLElement | null;
  setToolbarSlot: (element: HTMLDivElement | null) => void;
  titleLeadingSlot: HTMLElement | null;
  setTitleLeadingSlot: (element: HTMLDivElement | null) => void;
  children: ReactNode;
}

/**
 * Keep the header outside the panel so its toolbar slot survives tab switches.
 */
const WorkspaceBoard = ({
  title,
  titleHidden = false,
  toolbarSlot,
  setToolbarSlot,
  titleLeadingSlot,
  setTitleLeadingSlot,
  children,
}: WorkspaceBoardProps) => {
  const heading = (
    <Heading
      as="h2"
      data-testid="workspace-active-title"
      size="3"
      weight="medium"
      className="truncate"
    >
      {title}
    </Heading>
  );

  return (
    <Flex direction="column" flexGrow="1" p="3" className="min-h-0">
      <Flex
        align="center"
        justify="between"
        gap="4"
        mb={titleHidden ? "0" : "3"}
        pb={titleHidden ? "0" : "2"}
        className={cn(!titleHidden && "border-[color:var(--gray-a5)] border-b")}
      >
        {titleHidden ? (
          <VisuallyHidden asChild>{heading}</VisuallyHidden>
        ) : (
          <Flex align="center" gap="2" className="min-w-0">
            <Flex
              ref={setTitleLeadingSlot}
              data-testid="workspace-title-leading-slot"
              align="center"
              gap="2"
            />
            {heading}
          </Flex>
        )}
        <Flex
          ref={setToolbarSlot}
          data-testid="workspace-toolbar-slot"
          align="center"
          gap="2"
        />
      </Flex>
      <WorkspaceToolbarSlotProvider value={toolbarSlot}>
        <WorkspaceTitleLeadingSlotProvider value={titleLeadingSlot}>
          {children}
        </WorkspaceTitleLeadingSlotProvider>
      </WorkspaceToolbarSlotProvider>
    </Flex>
  );
};

const WorkspaceActionErrorAlert = ({ error }: { error: string | null }) =>
  error ? (
    <Text
      as="div"
      role="alert"
      aria-live="assertive"
      data-testid="workspace-action-error"
      size="2"
      color="red"
    >
      <Text as="span" weight="bold">
        Workspace action failed.
      </Text>{" "}
      {error}
    </Text>
  ) : null;

export const WorkspacePresentation = ({
  activeView,
  tabs,
  canCloseTab,
  selectWorkspace,
  selectOverview,
  openWorkspace,
  activePanel,
  navMode,
  navCollapsed,
  onToggleNavCollapsed,
  onOpenSettings,
  accountActions,
  nav,
  activeTabLabel,
  toolbarSlot,
  setToolbarSlot,
  titleLeadingSlot,
  setTitleLeadingSlot,
  renamingTabId,
  renamingTabLabel,
  pendingCloseId,
  pendingCloseLabel,
  restartOpen,
  workspaceActionError,
  renameError,
  deleteError,
  restartError,
  showPageActionError,
  renameField,
  renameFieldError,
  onCreateWorkspace,
  onDuplicateWorkspace,
  onBeginRename,
  onRenameSubmit,
  onRenameOpenChange,
  onRequestClose,
  onConfirmClose,
  onCloseOpenChange,
  onRestartOpenChange,
  onRestoreDefaults,
  onStartBlank,
}: WorkspacePresentationProps) => {
  // "overview" is the stable UI-only value for the Overview tab trigger.
  // All workspace IDs are UUIDs so there is no collision risk.
  const activeNavValue =
    activeView.type === "workspace" ? activeView.id : OVERVIEW_NAV_VALUE;

  const isOverview = activeView.type === "overview";
  const boardTitle = isOverview ? "Overview" : activeTabLabel;

  const handleNavChange = (value: string) => {
    if (value === OVERVIEW_NAV_VALUE) {
      selectOverview();
    } else {
      selectWorkspace(value);
    }
  };

  const sharedNavProps = {
    collapsed: navCollapsed,
    onToggleCollapsed: onToggleNavCollapsed,
    activeTab: activeNavValue,
    tabs,
    canCloseTab,
    onSelectTab: selectWorkspace,
    selectOverview,
    onCreateWorkspace,
    onDuplicateWorkspace,
    onOpenWorkspace: openWorkspace,
    onRequestClose,
    onBeginRename,
    onOpenSettings,
    accountActions,
  };

  const navElement =
    navMode === "top" ? (
      <WorkspaceNavPresentation
        {...sharedNavProps}
        mode="top"
        draggingTabId={nav.draggingTabId}
        stripRef={nav.stripRef}
        visibleTabs={nav.visibleTabs}
        overflowTabs={nav.overflowTabs}
        registerOverviewTrigger={nav.registerOverviewTrigger}
        registerTabTrigger={nav.registerTabTrigger}
        dropEdge={nav.dropEdge}
        onTabDragStart={nav.onDragStart}
        onTabDragEnd={nav.onDragEnd}
        onTabDragOver={nav.onDragOver}
        onTabDragLeave={nav.onDragLeave}
        onTabDrop={nav.onDrop}
      />
    ) : (
      <WorkspaceNavPresentation {...sharedNavProps} mode="left" />
    );

  return (
    <Flex
      data-testid="workspace-page"
      flexGrow="1"
      direction="column"
      minHeight="0"
      className="min-w-[1024px]"
    >
      {navMode === "top" ? (
        <Tabs.Root
          value={activeNavValue}
          onValueChange={handleNavChange}
          className={flexFill}
        >
          {navElement}
          <WorkspaceBoard
            title={boardTitle}
            titleHidden={isOverview}
            toolbarSlot={toolbarSlot}
            setToolbarSlot={setToolbarSlot}
            titleLeadingSlot={titleLeadingSlot}
            setTitleLeadingSlot={setTitleLeadingSlot}
          >
            {activeView.type === "overview" ? (
              <Tabs.Content value={OVERVIEW_NAV_VALUE} className={flexFill}>
                {activePanel}
              </Tabs.Content>
            ) : (
              <Tabs.Content value={activeView.id} className={flexFill}>
                {activePanel}
              </Tabs.Content>
            )}
          </WorkspaceBoard>
        </Tabs.Root>
      ) : (
        <Flex direction="row" flexGrow="1" align="stretch" minHeight="0">
          {navElement}
          <WorkspaceBoard
            title={boardTitle}
            titleHidden={isOverview}
            toolbarSlot={toolbarSlot}
            setToolbarSlot={setToolbarSlot}
            titleLeadingSlot={titleLeadingSlot}
            setTitleLeadingSlot={setTitleLeadingSlot}
          >
            <div className={flexFill}>{activePanel}</div>
          </WorkspaceBoard>
        </Flex>
      )}

      {showPageActionError ? (
        <WorkspaceActionErrorAlert error={workspaceActionError} />
      ) : null}

      <AlertDialog.Root open={restartOpen} onOpenChange={onRestartOpenChange}>
        <AlertDialog.Content maxWidth="420px">
          <AlertDialog.Title>Restart workspaces?</AlertDialog.Title>
          <AlertDialog.Description size="2" mb="4" color="gray">
            This replaces the shared workspace tabs and layouts and synchronizes
            the change across all open windows. Global groups are preserved.
            Choose restore defaults for the starter Home / Positions / Orders
            layout, or start blank for a single empty Home tab.
          </AlertDialog.Description>
          <WorkspaceActionErrorAlert error={workspaceActionError} />
          {restartError ? (
            <Text as="p" role="alert" size="2" mb="3" color="red">
              {restartError}
            </Text>
          ) : null}
          <Flex gap="3" justify="end" wrap="wrap">
            <AlertDialog.Cancel>
              <Button type="button" variant="soft" color="gray">
                Cancel
              </Button>
            </AlertDialog.Cancel>
            <Button type="button" variant="soft" onClick={onRestoreDefaults}>
              Restore defaults
            </Button>
            <Button type="button" color="red" onClick={onStartBlank}>
              Start blank
            </Button>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>

      <Dialog.Root
        open={renamingTabId !== null}
        onOpenChange={onRenameOpenChange}
      >
        <Dialog.Content maxWidth="420px">
          <Dialog.Title>Rename workspace</Dialog.Title>
          <Dialog.Description size="2" mb="3" color="gray">
            Choose a name for {renamingTabLabel}.
          </Dialog.Description>
          <WorkspaceActionErrorAlert error={workspaceActionError} />
          <form onSubmit={onRenameSubmit}>
            <Field label="Workspace name" error={renameFieldError} required>
              {(control) => (
                <TextField.Root
                  {...control}
                  {...renameField}
                  autoComplete="off"
                />
              )}
            </Field>
            {renameError ? (
              <Text as="p" role="alert" size="2" mt="2" color="red">
                {renameError}
              </Text>
            ) : null}
            <Flex gap="3" mt="4" justify="end">
              <Dialog.Close>
                <Button type="button" variant="soft" color="gray">
                  Cancel
                </Button>
              </Dialog.Close>
              <Button type="submit">Save</Button>
            </Flex>
          </form>
        </Dialog.Content>
      </Dialog.Root>

      <AlertDialog.Root
        open={pendingCloseId !== null}
        onOpenChange={onCloseOpenChange}
      >
        <AlertDialog.Content maxWidth="420px">
          <AlertDialog.Title>Remove {pendingCloseLabel}?</AlertDialog.Title>
          <AlertDialog.Description size="2" mb="4" color="gray">
            This removes the workspace and its layout and synchronizes the
            change across all open windows. This cannot be undone.
          </AlertDialog.Description>
          <WorkspaceActionErrorAlert error={workspaceActionError} />
          {deleteError ? (
            <Text as="p" role="alert" size="2" mb="3" color="red">
              {deleteError}
            </Text>
          ) : null}
          <Flex gap="3" justify="end">
            <AlertDialog.Cancel>
              <Button type="button" variant="soft" color="gray">
                Keep workspace
              </Button>
            </AlertDialog.Cancel>
            <Button type="button" color="red" onClick={onConfirmClose}>
              Remove workspace
            </Button>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>
    </Flex>
  );
};
